/**
 * Local Download Service - Save dice images directly to phone
 *
 * Downloads dice registration images to the device's filesystem,
 * allowing users to manually transfer them to their PC later.
 * Works on any device without network configuration.
 */

const SERVICE_TAG = '[LOCAL_DOWNLOAD]';

/** Download result */
export interface LocalDownloadResult {
  success: boolean;
  filename: string;
  size: number;
}

/**
 * Generate a filename for the dice image
 */
function generateFilename(
  deviceId: string,
  diceType: string,
  faceNumber: number,
  rotationIndex: number
): string {
  // Shorten deviceId to last 8 chars for readability
  const shortDeviceId = deviceId.slice(-8);
  return `dice_${shortDeviceId}_${diceType}_face${faceNumber.toString().padStart(2, '0')}_rot${rotationIndex.toString().padStart(2, '0')}.jpg`;
}

/**
 * Local Download Service for saving images to device
 */
export const localDownloadService = {
  /**
   * Download image to device
   *
   * Uses the browser's download functionality to save images.
   * On mobile, images go to the Downloads folder or Photos app.
   *
   * @param deviceId - Device identifier
   * @param diceType - Dice type (d4, d6, etc.)
   * @param faceNumber - Face number (1-based)
   * @param imageBlob - Image data as Blob
   * @param rotationIndex - Rotation variant index (0-based)
   * @returns Download result
   */
  async downloadImage(
    deviceId: string,
    diceType: string,
    faceNumber: number,
    imageBlob: Blob,
    rotationIndex: number = 0
  ): Promise<LocalDownloadResult> {
    const filename = generateFilename(deviceId, diceType, faceNumber, rotationIndex);

    console.info(`${SERVICE_TAG} Downloading image`, {
      filename,
      diceType,
      faceNumber,
      rotationIndex,
      blobSize: imageBlob.size
    });

    try {
      // Create object URL for the blob
      const url = URL.createObjectURL(imageBlob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up object URL
      URL.revokeObjectURL(url);

      console.info(`${SERVICE_TAG} Download triggered`, { filename, size: imageBlob.size });

      return {
        success: true,
        filename,
        size: imageBlob.size
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Download failed';
      console.error(`${SERVICE_TAG} Download failed`, { error: message, filename });
      throw new Error(`Failed to download image: ${message}`);
    }
  },

  /**
   * Download multiple images as a batch
   * Note: Some browsers may block rapid sequential downloads
   *
   * @param images - Array of image data
   * @returns Array of download results
   */
  async downloadBatch(
    images: Array<{
      deviceId: string;
      diceType: string;
      faceNumber: number;
      imageBlob: Blob;
      rotationIndex: number;
    }>
  ): Promise<LocalDownloadResult[]> {
    console.info(`${SERVICE_TAG} Starting batch download`, { count: images.length });

    const results: LocalDownloadResult[] = [];

    for (const image of images) {
      // Add delay between downloads to avoid browser blocking
      if (results.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const result = await this.downloadImage(
        image.deviceId,
        image.diceType,
        image.faceNumber,
        image.imageBlob,
        image.rotationIndex
      );
      results.push(result);
    }

    console.info(`${SERVICE_TAG} Batch download complete`, {
      total: images.length,
      successful: results.filter(r => r.success).length
    });

    return results;
  },

  /**
   * Check if download is supported
   * (Always true for modern browsers, but good for future-proofing)
   */
  isSupported(): boolean {
    return typeof document !== 'undefined' && 'createElement' in document;
  }
};
